import {Column, ColumnDef} from "@tanstack/react-table";
import {EditIcon} from "lucide-react";
import {useUsersTranslation} from '@/i18n/hooks';
import {Link} from "@/components/tailwind/link.tsx";
import {sortableColumnHeader} from "@/components/data-table.tsx";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip.tsx";
import {formatDateTimeString} from "@/lib/utils.ts";

// Compact placeholder for a timestamp we never recorded (predates the tracking feature).
const UNKNOWN_TIMESTAMP = "—";

export type UserAccount = {
    id: string,
    username: string,
    name: string,
    active: boolean,
    role: "ADMIN" | "USER",
    // The API serializes these as ISO-8601 strings and handleResponse() does not revive dates,
    // so they are strings at runtime, not Date objects.
    createdAt: string,
    updatedAt: string | null,
    passwordChangedAt?: string | null
}

function RoleCell({ role }: { role: "ADMIN" | "USER" }) {
    const { t } = useUsersTranslation();
    return <>{t(`userList.table.roleColumn.${role}`)}</>;
}

function ActiveCell({ active }: { active: boolean }) {
    const { t } = useUsersTranslation();
    return <>{t(`userList.table.activeColumn.${active}`)}</>;
}

export function PasswordChangedAtCell({ value }: { value?: string | null }) {
    const { t } = useUsersTranslation();
    // Keep the timestamp on a single line so it acts as the column's width anchor, letting the
    // (deliberately wrapped) header sit within that width instead of widening the column.
    if (!value) {
        // The bare em-dash carries no meaning alone; expose the reason to mouse and screen readers.
        const unknownLabel = t("userList.table.passwordChangedAtUnknown");
        return (
            <span className="whitespace-nowrap" title={unknownLabel} aria-label={unknownLabel}>
                {UNKNOWN_TIMESTAMP}
            </span>
        );
    }
    return <span className="whitespace-nowrap">{formatDateTimeString(value)}</span>;
}

// Dedicated header: reuses the shared sortable header (sorting + icons) but lets the abbreviated,
// pre-line label wrap to two lines and exposes the full wording via a hover tooltip. The shared
// header is left untouched so other tables are unaffected.
function PasswordChangedAtHeader({ column }: { column: Column<UserAccount, unknown> }) {
    const { t } = useUsersTranslation();
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="whitespace-pre-line">
                        {sortableColumnHeader(column, "userList.table.headers.passwordChangedAt", "users")}
                    </span>
                </TooltipTrigger>
                <TooltipContent>{t("userList.table.headers.passwordChangedAtFull")}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export const columns: ColumnDef<UserAccount>[] = [
    {
        accessorKey: "name",
        header: ({column}) =>
            sortableColumnHeader(column, "userList.table.headers.name", 'users')

    },
    {
        accessorKey: "username",
        header: ({column}) =>
            sortableColumnHeader(column, "userList.table.headers.username", 'users')

    },
    {
        accessorKey: "role",
        header: ({column}) =>
            sortableColumnHeader(column, "userList.table.headers.role", 'users'),
        cell: ({row}) => <RoleCell role={row.original.role} />
    },
    {
        accessorKey: "active",
        header: ({column}) =>
            sortableColumnHeader(column, "userList.table.headers.active", 'users'),
        cell: ({row}) => <ActiveCell active={row.original.active} />
    },
    {
        id: "passwordChangedAt",
        // Coalesce null to undefined so TanStack's sortUndefined can place never-recorded values last.
        accessorFn: (row) => row.passwordChangedAt ?? undefined,
        header: ({column}) => <PasswordChangedAtHeader column={column} />,
        cell: ({row}) => <PasswordChangedAtCell value={row.original.passwordChangedAt} />,
        // Never-recorded values sort last in both directions (handled before the desc inversion).
        sortUndefined: "last",
        // Both values are present ISO-8601 UTC strings here; compare them chronologically.
        sortingFn: (rowA, rowB, columnId) =>
            new Date(rowA.getValue<string>(columnId)).getTime() -
            new Date(rowB.getValue<string>(columnId)).getTime(),
    },
    {
        id: "actions",
        cell: ({row}) => {
            const user = row.original

            return (
                <Link href={`/admin/users/${user.id}`} data-testid={`edit-user-${user.id}`}>
                    <EditIcon/>
                </Link>
            )
        }

    }

]
